import { DayPicker, type DayPickerProps } from "react-day-picker"
import { ChevronDown, ChevronLeft, ChevronRight } from "react-feather"

import { buttonVariants } from "@/components/ui/button/button"
import { cn } from "@/lib/utils"

export type CalendarProps = DayPickerProps

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const currentYear = new Date().getFullYear()
  const defaultStartMonth = new Date(currentYear - 100, 0)
  const defaultEndMonth = new Date(currentYear + 10, 11)

  return (
    <DayPicker
      navLayout="around"
      captionLayout={props.captionLayout ?? "dropdown"}
      startMonth={props.startMonth ?? defaultStartMonth}
      endMonth={props.endMonth ?? defaultEndMonth}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "grid grid-cols-[auto_1fr_auto] gap-y-2 items-center",
        month_caption: "flex items-center justify-center",
        caption_label: "text-sm font-medium flex items-center gap-1",
        nav: "space-x-1 flex items-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        dropdowns: "flex items-center gap-2",
        dropdown: "absolute inset-0 w-full h-full opacity-0 cursor-pointer",
        dropdown_root:
          "relative inline-flex items-center rounded-md border border-input bg-background pl-3 pr-2 py-1 cursor-pointer",
        chevron: "size-4",
        months_dropdown: "",
        years_dropdown: "",
        month_grid: "col-span-3 border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary rounded-md text-primary-foreground [&>button:hover]:bg-primary [&>button:hover]:text-primary-foreground [&>button:focus]:bg-primary [&>button:focus]:text-primary-foreground",
        today: "bg-accent rounded-md text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className }) => {
          const Icon =
            orientation === "left"
              ? ChevronLeft
              : orientation === "down"
                ? ChevronDown
                : ChevronRight
          return <Icon className={className ?? "h-4 w-4"} />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
